import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";


import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSnackbar from "components/SoftSnackbar";
import SoftSelect from "components/SoftSelect";

import { useAuth } from "AuthContext";
import { API_URL } from "config";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import FlagLoader from "layouts/flagloader";

const PAGE_PERMISSIONS = ["user_hits.page.view", "user_hits", "viewUserHits"];

const CARD_CONFIG = [
  { key: "completes", label: "Completes", detail: "Overall", icon: "done", color: "#0ea5c6", background: "#e7f8fd" },
  { key: "accepted", label: "Accepted", detail: "Latest client decision", icon: "check", color: "#159570", background: "#e8f8f1" },
  { key: "rejected", label: "Rejected", detail: "Latest client decision", icon: "close", color: "#d55063", background: "#fdecef" },
  { key: "pending", label: "Pending", detail: "Awaiting final IDs", icon: "more_horiz", color: "#b67800", background: "#fff4d9" },
  { key: "acceptance_rate", label: "Accepted %", detail: "Accepted out of all completes", icon: "percent", color: "#5b69bb", background: "#eef0ff", suffix: "%" },
  { key: "active_users", label: "Active Users", detail: "Employees with completes", icon: "group", color: "#7756c8", background: "#f1ebff" },
];

const FIELD_CONFIG = [
  { key: "user", label: "User", grid: "1.8fr" },
  { key: "user_code", label: "Employee Code", grid: "1fr" },
  { key: "branch", label: "Branch", grid: "1.3fr" },
  { key: "sub_branch", label: "Sub-branch", grid: "1.3fr" },
  { key: "completes", label: "Completes", grid: "0.78fr" },
  { key: "accepted", label: "Accepted", grid: "0.78fr" },
  { key: "rejected", label: "Rejected", grid: "0.78fr" },
  { key: "pending", label: "Pending", grid: "0.78fr" },
  { key: "performance", label: "Performance", grid: "1.7fr" },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: "42px !important",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-input": {
    height: "42px !important",
    padding: "0 12px !important",
    display: "flex",
    alignItems: "center",
  },
};

const filterLabelSx = {
  display: "block",
  mb: 0.7,
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.35,
  color: "#52627e",
};

const filterSelectSx = {
  "& .select__control": {
    minHeight: "42px !important",
    height: "42px !important",
    borderRadius: "8px",
  },
  "& .select__value-container": {
    height: "40px !important",
    padding: "0 12px !important",
  },
};

const emptyVisibility = {
  filters: {},
  cards: {},
  fields: {},
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const optionList = (items, labelKey = "name") =>
  asArray(items).map((item) => ({
    value: item.id,
    label: item[labelKey] || "N/A",
  }));

function MetricCard({ config, value }) {
  const displayValue = config.suffix ? `${Number(value || 0).toFixed(1)}${config.suffix}` : formatNumber(value);

  return (
    <Card
      sx={{
        minHeight: 136,
        borderRadius: "18px",
        boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
        border: "1px solid #eef2f7",
      }}
    >
      <SoftBox p={2.1} display="flex" alignItems="center" gap={1.8}>
        <SoftBox
          sx={{
            width: 58,
            height: 58,
            borderRadius: "17px",
            background: config.background,
            color: config.color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 25, color: config.color }}>{config.icon}</Icon>
        </SoftBox>
        <SoftBox minWidth={0}>
          <SoftTypography
            sx={{
              color: "#8a99b3",
              fontWeight: 600,
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {config.label}
          </SoftTypography>
          <SoftTypography sx={{ color: "#17264a", fontSize: "29px", fontWeight: 700, lineHeight: 1.2, mt: 0.35 }}>
            {displayValue}
          </SoftTypography>
          <SoftTypography sx={{ color: "#94a3b8", fontSize: "11px", mt: 0.25 }}>
            {config.detail}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

function PerformanceCell({ performance }) {
  const accepted = Number(performance?.accepted_percent || 0);
  const rejected = Number(performance?.rejected_percent || 0);
  const pending = Number(performance?.pending_percent || 0);

  return (
    <SoftBox sx={{ minWidth: 0, width: "100%" }}>
      <SoftTypography sx={{ fontSize: "12px", color: "#334155", fontWeight: 600 }}>
        Accepted {accepted.toFixed(1)}%
      </SoftTypography>
      <SoftBox sx={{ display: "flex", height: 6, overflow: "hidden", borderRadius: 8, bgcolor: "#e8edf4", mt: 0.65 }}>
        <Box sx={{ width: `${accepted}%`, bgcolor: "#24ba8a" }} />
        <Box sx={{ width: `${rejected}%`, bgcolor: "#e96b7b" }} />
        <Box sx={{ width: `${pending}%`, bgcolor: "#e5ad38" }} />
      </SoftBox>
      <SoftTypography sx={{ fontSize: "10px", color: "#8a99b3", mt: 0.5 }}>
        Rejected {rejected.toFixed(1)}% · Awaited {pending.toFixed(1)}%
      </SoftTypography>
    </SoftBox>
  );
}

MetricCard.propTypes = {
  config: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    detail: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    suffix: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PerformanceCell.propTypes = {
  performance: PropTypes.shape({
    accepted_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rejected_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pending_percent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};
function UserHits() {
  const token = localStorage.getItem("authToken");
  const { permissions = [], userPermission = [], isAuthReady } = useAuth();
  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );
  const canOpenPage = hasPermission(grantedPermissions, PAGE_PERMISSIONS);

  const [rows, setRows] = useState([]);
  const [cards, setCards] = useState({});
  const [visibility, setVisibility] = useState(emptyVisibility);
  const [branches, setBranches] = useState([]);
  const [subBranches, setSubBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSubBranch, setSelectedSubBranch] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [show, setShow] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0, last_page: 1 });

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const isVisible = useCallback(
    (group, key) => {
      if (typeof visibility?.[group]?.[key] === "boolean") return visibility[group][key];
      return hasPermission(grantedPermissions, `user_hits.${group}.${key}.view`);
    },
    [grantedPermissions, visibility]
  );

  const setFilterOptions = useCallback((payload = {}) => {
    setBranches(optionList(payload.branches));
    setSubBranches(optionList(payload.sub_branches));
    setShifts(optionList(payload.shifts, "label"));
    setUsers(optionList(payload.users));
    setClients(optionList(payload.clients));
  }, []);

  const fetchFilterOptions = useCallback(
    async (filters = {}) => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") params.set(key, value);
      });

      try {
        const response = await fetch(
          `${API_URL}/studies/user-data/filters${params.toString() ? `?${params.toString()}` : ""}`,
          { method: "GET", headers: authHeaders }
        );
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || "Failed to load user data filters");
        }

        setVisibility(data.visibility || emptyVisibility);
        setFilterOptions(data.data || {});
      } catch (error) {
        setBranches([]);
        setSubBranches([]);
        setShifts([]);
        setUsers([]);
        setClients([]);
        setMessage(error.message || "Failed to load user data filters");
        setShow(true);
      }
    },
    [authHeaders, setFilterOptions]
  );

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(currentPage), per_page: String(perPage) });
    if (selectedBranch?.value) params.set("branch_id", selectedBranch.value);
    if (selectedSubBranch?.value) params.set("subbranch_id", selectedSubBranch.value);
    if (selectedShift?.value) params.set("shift_id", selectedShift.value);
    if (selectedUser?.value) params.set("user_id", selectedUser.value);
    if (selectedClient?.value) params.set("client_id", selectedClient.value);
    if (fromDate) params.set("start_date", fromDate);
    if (toDate) params.set("end_date", toDate);
    if (searchQuery.trim()) params.set("q", searchQuery.trim());

    try {
      const response = await fetch(`${API_URL}/studies/user-data?${params.toString()}`, {
        method: "GET",
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to load user data");
      }

      setVisibility(data.visibility || emptyVisibility);
      setCards(data.cards || {});
      setRows(asArray(data.data));
      setPagination(data.pagination || { total: 0, from: 0, to: 0, last_page: 1 });
    } catch (error) {
      setRows([]);
      setCards({});
      setPagination({ total: 0, from: 0, to: 0, last_page: 1 });
      setMessage(error.message || "Failed to load user data");
      setShow(true);
    } finally {
      setLoading(false);
    }
  }, [
    authHeaders,
    currentPage,
    fromDate,
    perPage,
    searchQuery,
    selectedBranch,
    selectedClient,
    selectedShift,
    selectedSubBranch,
    selectedUser,
    toDate,
  ]);

  useEffect(() => {
    if (isAuthReady && canOpenPage) fetchFilterOptions();
  }, [canOpenPage, fetchFilterOptions, isAuthReady]);

  useEffect(() => {
    if (!isAuthReady || !canOpenPage) return undefined;
    const timer = window.setTimeout(fetchUserData, searchQuery ? 280 : 0);
    return () => window.clearTimeout(timer);
  }, [canOpenPage, fetchUserData, isAuthReady, searchQuery]);

  const filterParamsFor = (overrides = {}) => ({
    branch_id: overrides.branch_id ?? selectedBranch?.value,
    subbranch_id: overrides.subbranch_id ?? selectedSubBranch?.value,
    shift_id: overrides.shift_id ?? selectedShift?.value,
    user_id: overrides.user_id ?? selectedUser?.value,
    client_id: overrides.client_id ?? selectedClient?.value,
  });

  const handleBranchChange = (option) => {
    setCurrentPage(1);
    setSelectedBranch(option || null);
    setSelectedSubBranch(null);
    setSelectedShift(null);
    setSelectedUser(null);
    fetchFilterOptions({ ...filterParamsFor({ branch_id: option?.value, subbranch_id: null, shift_id: null, user_id: null }) });
  };

  const handleSubBranchChange = (option) => {
    setCurrentPage(1);
    setSelectedSubBranch(option || null);
    setSelectedShift(null);
    setSelectedUser(null);
    fetchFilterOptions({ ...filterParamsFor({ subbranch_id: option?.value, shift_id: null, user_id: null }) });
  };

  const handleShiftChange = (option) => {
    setCurrentPage(1);
    setSelectedShift(option || null);
    setSelectedUser(null);
    fetchFilterOptions({ ...filterParamsFor({ shift_id: option?.value, user_id: null }) });
  };

  const handleUserChange = (option) => {
    setCurrentPage(1);
    setSelectedUser(option || null);
  };

  const clearFilters = () => {
    setCurrentPage(1);
    setSelectedBranch(null);
    setSelectedSubBranch(null);
    setSelectedShift(null);
    setSelectedUser(null);
    setSelectedClient(null);
    setFromDate("");
    setToDate("");
    setSearchQuery("");
    fetchFilterOptions();
  };

  const visibleCards = CARD_CONFIG.filter((config) => isVisible("cards", config.key));
  const visibleFields = FIELD_CONFIG.filter((field) => isVisible("fields", field.key));
  const totalPages = Math.max(1, Number(pagination?.last_page || 1));
  const tableGridTemplate = visibleFields.map((field) => `minmax(0, ${field.grid})`).join(" ");

  if (isAuthReady && !canOpenPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Icon sx={{ color: "#cbd5e1", fontSize: 44 }}>lock</Icon>
          <SoftTypography sx={{ mt: 1, color: "#475569" }}>You do not have permission to view User Data.</SoftTypography>
        </Card>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color="primary"
        icon="notifications"
        title="User Data"
        content={message}
        open={show}
        close={() => setShow(false)}
        onClose={() => setShow(false)}
      />

      <SoftBox mb={2.4}>
        <SoftTypography sx={{ color: "#17264a", fontSize: { xs: "30px", md: "38px" }, fontWeight: 700, lineHeight: 1.1 }}>
          User Data
        </SoftTypography>
        <SoftTypography sx={{ color: "#7182a2", fontSize: "15px", mt: 0.6 }}>
          Employee completes and their latest client acceptance status.
        </SoftTypography>
      </SoftBox>

      {visibleCards.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(245px, 1fr))",
            gap: 1.8,
            mb: 2,
          }}
        >
          {visibleCards.map((config) => (
            <MetricCard key={config.key} config={config} value={cards[config.key]} />
          ))}
        </Box>
      )}

      <Card sx={{ mb: 2, p: { xs: 1.8, md: 2.2 }, borderRadius: "18px", overflow: "visible", border: "1px solid #eef2f7" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
            gap: 1.75,
            alignItems: "end",
          }}
        >
          {isVisible("filters", "search") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Search user</SoftTypography>
              <SoftInput
                fullWidth
                value={searchQuery}
                onChange={(event) => {
                  setCurrentPage(1);
                  setSearchQuery(event.target.value);
                }}
                placeholder="Name, email or employee ID..."
                sx={inputSx}
              />
            </Box>
          )}

          {isVisible("filters", "branch") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Branch</SoftTypography>
              <SoftSelect options={branches} value={selectedBranch} onChange={handleBranchChange} placeholder="All branches" isClearable sx={filterSelectSx} />
            </Box>
          )}

          {isVisible("filters", "subbranch") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Sub-branch</SoftTypography>
              <SoftSelect
                options={subBranches}
                value={selectedSubBranch}
                onChange={handleSubBranchChange}
                placeholder="All sub-branches"
                isDisabled={isVisible("filters", "branch") && !selectedBranch}
                isClearable
                sx={filterSelectSx}
              />
            </Box>
          )}

          {isVisible("filters", "shift") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Shift</SoftTypography>
              <SoftSelect
                options={shifts}
                value={selectedShift}
                onChange={handleShiftChange}
                placeholder="All shifts"
                isDisabled={isVisible("filters", "subbranch") && !selectedSubBranch}
                isClearable
                sx={filterSelectSx}
              />
            </Box>
          )}

          {isVisible("filters", "user") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Users</SoftTypography>
              <SoftSelect
                options={users}
                value={selectedUser}
                onChange={handleUserChange}
                placeholder="All users"
                isDisabled={isVisible("filters", "shift") && !selectedShift}
                isClearable
                sx={filterSelectSx}
              />
            </Box>
          )}

          {isVisible("filters", "client") && (
            <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
              <SoftTypography variant="caption" sx={filterLabelSx}>Client</SoftTypography>
              <SoftSelect
                options={clients}
                value={selectedClient}
                onChange={(option) => {
                  setCurrentPage(1);
                  setSelectedClient(option || null);
                }}
                placeholder="All clients"
                isClearable
                sx={filterSelectSx}
              />
            </Box>
          )}

          {isVisible("filters", "date_range") && (
            <>
              <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
                <SoftTypography variant="caption" sx={filterLabelSx}>Start Date</SoftTypography>
                <SoftInput type="datetime-local" fullWidth value={fromDate} onChange={(event) => { setCurrentPage(1); setFromDate(event.target.value); }} sx={inputSx} />
              </Box>
              <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
                <SoftTypography variant="caption" sx={filterLabelSx}>End Date</SoftTypography>
                <SoftInput type="datetime-local" fullWidth value={toDate} onChange={(event) => { setCurrentPage(1); setToDate(event.target.value); }} sx={inputSx} />
              </Box>
            </>
          )}

          <Box sx={{ minWidth: 0, gridColumn: "auto" }}>
            <button type="button" onClick={clearFilters} className="btn btn-light" style={{ width: "100%", height: 42, borderRadius: 8, color: "#64748b", fontWeight: 600, whiteSpace: "nowrap" }}>
              Clear filters
            </button>
          </Box>
        </Box>
      </Card>
      <Card sx={{ overflow: "hidden", borderRadius: "18px", border: "1px solid #e8edf4" }}>
        <SoftBox px={{ xs: 1.8, md: 2.4 }} py={1.9} sx={{ borderBottom: "1px solid #e8edf4" }}>
          <SoftTypography sx={{ color: "#17264a", fontSize: "24px", fontWeight: 700 }}>Employee performance</SoftTypography>
          <SoftTypography sx={{ color: "#7182a2", fontSize: "13px", mt: 0.45 }}>
            {[
              `${formatNumber(pagination?.total)} employees`,
              Object.prototype.hasOwnProperty.call(cards, "completes") && `${formatNumber(cards.completes)} completes`,
              Object.prototype.hasOwnProperty.call(cards, "acceptance_rate") && `${Number(cards.acceptance_rate || 0).toFixed(1)}% accepted`,
            ].filter(Boolean).join(" · ")}
          </SoftTypography>
        </SoftBox>

        {loading ? (
          <SoftBox display="flex" justifyContent="center" alignItems="center" height="350px"><FlagLoader /></SoftBox>
        ) : visibleFields.length === 0 ? (
          <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="350px" px={2} textAlign="center">
            <Icon sx={{ fontSize: 46, color: "#cbd5e1", mb: 1 }}>visibility_off</Icon>
            <SoftTypography sx={{ color: "#64748b", fontSize: "14px" }}>No User Data table field is assigned to this role.</SoftTypography>
          </SoftBox>
        ) : rows.length === 0 ? (
          <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="350px">
            <Icon sx={{ fontSize: 46, color: "#cbd5e1", mb: 1 }}>bar_chart</Icon>
            <SoftTypography sx={{ color: "#64748b", fontSize: "14px" }}>No data found</SoftTypography>
          </SoftBox>
        ) : (
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                columnGap: 1.25,
                px: { xs: 1.5, md: 2.4 },
                py: 1.45,
                bgcolor: "#fafbfd",
                borderBottom: "1px solid #e8edf4",
              }}
            >
              {visibleFields.map((field) => (
                <SoftTypography
                  key={field.key}
                  sx={{
                    minWidth: 0,
                    color: "#8a99b3",
                    fontWeight: 700,
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                  }}
                >
                  {field.label}
                </SoftTypography>
              ))}
            </Box>

            {rows.map((row, index) => (
              <Box
                key={`${currentPage}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: tableGridTemplate,
                  columnGap: 1.25,
                  alignItems: "center",
                  px: { xs: 1.5, md: 2.4 },
                  py: 1.9,
                  minHeight: 86,
                  borderBottom: "1px solid #edf1f5",
                  transition: "background-color 0.18s ease",
                  "&:hover": { bgcolor: "#fbfcfe" },
                }}
              >
                {visibleFields.map((field) => (
                  <Box key={field.key} sx={{ minWidth: 0, textAlign: "left", overflowWrap: "anywhere" }}>
                    {field.key === "user" && (
                      <>
                        <SoftTypography sx={{ color: "#17264a", fontSize: "14px", fontWeight: 700, overflowWrap: "anywhere" }}>{row.user?.name || "N/A"}</SoftTypography>
                        {row.user?.email && <SoftTypography sx={{ color: "#94a3b8", fontSize: "11px", mt: 0.2, overflowWrap: "anywhere" }}>{row.user.email}</SoftTypography>}
                      </>
                    )}
                    {field.key === "user_code" && <SoftTypography sx={{ color: "#17264a", fontSize: "13px", fontWeight: 600, overflowWrap: "anywhere" }}>{row.user_code || "N/A"}</SoftTypography>}
                    {field.key === "branch" && <SoftTypography sx={{ fontSize: "13px", overflowWrap: "anywhere" }}>{row.branch || "N/A"}</SoftTypography>}
                    {field.key === "sub_branch" && <SoftTypography sx={{ fontSize: "13px", overflowWrap: "anywhere" }}>{row.sub_branch || "N/A"}</SoftTypography>}
                    {field.key === "completes" && <SoftTypography sx={{ color: "#17264a", fontSize: "17px", fontWeight: 700 }}>{formatNumber(row.completes)}</SoftTypography>}
                    {field.key === "accepted" && <Box component="span" sx={{ display: "inline-flex", px: 1.1, py: 0.55, borderRadius: "10px", bgcolor: "#e8f8f1", color: "#159570", fontSize: "13px", fontWeight: 700 }}>{formatNumber(row.accepted)}</Box>}
                    {field.key === "rejected" && <Box component="span" sx={{ display: "inline-flex", px: 1.1, py: 0.55, borderRadius: "10px", bgcolor: "#fdecef", color: "#d55063", fontSize: "13px", fontWeight: 700 }}>{formatNumber(row.rejected)}</Box>}
                    {field.key === "pending" && <Box component="span" sx={{ display: "inline-flex", px: 1.1, py: 0.55, borderRadius: "10px", bgcolor: "#fff4d9", color: "#a76e00", fontSize: "13px", fontWeight: 700 }}>{formatNumber(row.pending)}</Box>}
                    {field.key === "performance" && <PerformanceCell performance={row.performance} />}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}

        {visibleFields.length > 0 && !loading && (
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" px={{ xs: 1.8, md: 2.4 }} py={1.5} gap={1.5} flexWrap="wrap">
            <SoftTypography sx={{ color: "#64748b", fontSize: "13px" }}>
              Showing {Number(pagination?.from || 0)} to {Number(pagination?.to || 0)} of {formatNumber(pagination?.total)} entries
            </SoftTypography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <SoftPagination item onClick={() => setCurrentPage(1)} disabled={currentPage === 1}><Icon sx={{ fontSize: 16 }}>first_page</Icon></SoftPagination>
              <SoftPagination item onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}><Icon sx={{ fontSize: 16 }}>chevron_left</Icon></SoftPagination>
              <SoftTypography sx={{ color: "#475569", fontSize: "13px", px: 1 }}>Page {currentPage} / {totalPages}</SoftTypography>
              <SoftPagination item onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}><Icon sx={{ fontSize: 16 }}>chevron_right</Icon></SoftPagination>
              <SoftPagination item onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}><Icon sx={{ fontSize: 16 }}>last_page</Icon></SoftPagination>
            </Box>
          </SoftBox>
        )}
      </Card>

      <Footer />
    </DashboardLayout>
  );
}

export default UserHits;
